import React from "react";
import { Box, Text, Image, Spinner, useToast } from "@chakra-ui/react";
import { useDropzone } from "react-dropzone";
import { uploadCardImage } from "../../../../services/upload.service";

const ACCEPTED_TYPES = ["image/jpeg", "image/png", "image/webp"];

const CardImageUpload = ({ label, value, onChange, isInvalid, errorMessage }) => {
  const [uploading, setUploading] = React.useState(false);
  const [previewUrl, setPreviewUrl] = React.useState(null);
  const toast = useToast();

  React.useEffect(() => {
    return () => {
      if (previewUrl) URL.revokeObjectURL(previewUrl);
    };
  }, [previewUrl]);

  const onDrop = React.useCallback(
    async (acceptedFiles, fileRejections) => {
      if (fileRejections.length > 0) {
        toast({
          title: "Invalid file",
          description: "Only JPEG, PNG, or WEBP images under 5MB are allowed.",
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        return;
      }

      const file = acceptedFiles[0];
      if (!file) return;

      setPreviewUrl(URL.createObjectURL(file));
      setUploading(true);
      try {
        const url = await uploadCardImage(file);
        onChange(url);
        setPreviewUrl(null);
      } catch (error) {
        toast({
          title: "Upload failed",
          description: error.response?.data?.message || error.message,
          status: "error",
          duration: 4000,
          isClosable: true,
        });
        setPreviewUrl(null);
      } finally {
        setUploading(false);
      }
    },
    [onChange, toast]
  );

  const { getRootProps, getInputProps, isDragActive } = useDropzone({
    onDrop,
    accept: ACCEPTED_TYPES,
    maxFiles: 1,
    maxSize: 5 * 1024 * 1024,
    disabled: uploading,
  });

  const displaySrc = previewUrl || value;

  return (
    <Box w="100%">
      <Text fontSize="sm" fontWeight="500" mb={1}>
        {label}
      </Text>
      <Box
        {...getRootProps()}
        borderWidth={isInvalid ? "2px" : "1px"}
        borderStyle="dashed"
        borderColor={
          isInvalid ? "red.400" : isDragActive ? "blue.400" : "gray.300"
        }
        borderRadius="md"
        p={3}
        textAlign="center"
        cursor={uploading ? "not-allowed" : "pointer"}
        bg={isDragActive ? "blue.50" : "transparent"}
      >
        <input {...getInputProps()} />
        {displaySrc ? (
          <Box position="relative">
            <Image
              src={displaySrc}
              alt={label}
              maxH="120px"
              mx="auto"
              mb={2}
              objectFit="contain"
              opacity={uploading ? 0.5 : 1}
            />
            {uploading ? (
              <Spinner
                size="md"
                position="absolute"
                top="40%"
                left="50%"
                transform="translate(-50%, -50%)"
              />
            ) : (
              <Text fontSize="xs" color="gray.500">
                Click or drag to replace
              </Text>
            )}
          </Box>
        ) : (
          <Text fontSize="sm" color="gray.500">
            Drag & drop an image here, or click to select
          </Text>
        )}
      </Box>
      {isInvalid && errorMessage && (
        <Text fontSize="sm" color="red.500" mt={1}>
          {errorMessage}
        </Text>
      )}
    </Box>
  );
};

export default CardImageUpload;
